import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

/**
 * Invoice hooks — read `orders.v_invoices_all` and `orders.v_invoice_lines`,
 * which bridge `ops.qbo_invoices` and `ops.qbo_invoice_lines` under RLS
 * scoped to the current customer.
 */

export interface InvoiceRow {
  qboInvoiceId: string;
  docNumber: string | null;
  customerName: string;
  txnDate: string;
  dueDate: string;
  totalAmount: number;
  balance: number;
  status: string | null;
  memo: string | null;
  txnType: string;
  isOpen: boolean;
  daysPastDue: number | null;
  agingBucket:
    | 'paid'
    | 'current'
    | 'past_due_30'
    | 'past_due_60'
    | 'past_due_90'
    | 'past_due_90_plus';
  paymentUrl: string | null;
  sfJobId: string | null;
  deliveryLine1: string | null;
  deliveryCity: string | null;
  deliveryState: string | null;
  deliveryPostal: string | null;
  /** rental classification (from revenue_line) */
  isRental: boolean;
  rentalKind: 'tank' | 'equipment' | null;
  hasTankRental: boolean;
  hasEquipmentRental: boolean;
  /** this invoice's rental kind is enrolled in recurring auto-pay */
  rentalAutopay: boolean;
  /**
   * The customer's own PO number for this invoice, when the order that produced
   * it carried one. Resolved in `orders.v_invoices_all` (migration
   * 20260917171224) — QuickBooks keeps it only as a $0 description line the
   * mirror does not carry. null when there genuinely isn't one.
   */
  poNumber: string | null;
}

interface InvoiceDbRow {
  qbo_invoice_id: string;
  doc_number: string | null;
  customer_name: string;
  txn_date: string;
  due_date: string;
  total_amount: number | string;
  balance: number | string;
  status: string | null;
  memo: string | null;
  txn_type: string;
  is_open: boolean;
  days_past_due: number | null;
  aging_bucket: InvoiceRow['agingBucket'];
  invoice_payment_url: string | null;
  sf_job_id: string | null;
  delivery_line1: string | null;
  delivery_city: string | null;
  delivery_state: string | null;
  delivery_postal: string | null;
  is_rental: boolean | null;
  rental_kind: 'tank' | 'equipment' | null;
  has_tank_rental: boolean | null;
  has_equipment_rental: boolean | null;
  rental_autopay: boolean | null;
  po_number: string | null;
}

const SELECT_COLS =
  'qbo_invoice_id, doc_number, customer_name, txn_date, due_date, total_amount, balance, status, memo, txn_type, is_open, days_past_due, aging_bucket, invoice_payment_url, sf_job_id, delivery_line1, delivery_city, delivery_state, delivery_postal, is_rental, rental_kind, has_tank_rental, has_equipment_rental, rental_autopay, po_number';

function mapRow(r: InvoiceDbRow): InvoiceRow {
  return {
    qboInvoiceId: r.qbo_invoice_id,
    docNumber: r.doc_number,
    customerName: r.customer_name,
    txnDate: r.txn_date,
    dueDate: r.due_date,
    totalAmount: Number(r.total_amount ?? 0),
    balance: Number(r.balance ?? 0),
    status: r.status,
    memo: r.memo,
    txnType: r.txn_type,
    isOpen: r.is_open,
    daysPastDue: r.days_past_due,
    agingBucket: r.aging_bucket,
    paymentUrl: r.invoice_payment_url,
    sfJobId: r.sf_job_id,
    deliveryLine1: r.delivery_line1,
    deliveryCity: r.delivery_city,
    deliveryState: r.delivery_state,
    deliveryPostal: r.delivery_postal,
    isRental: r.is_rental === true,
    rentalKind: r.rental_kind ?? null,
    hasTankRental: r.has_tank_rental === true,
    hasEquipmentRental: r.has_equipment_rental === true,
    rentalAutopay: r.rental_autopay === true,
    poNumber: (r.po_number ?? '').trim() || null,
  };
}

/** All invoices for the current customer, newest first. */
export function useInvoices() {
  return useQuery<InvoiceRow[]>({
    queryKey: ['invoices', 'all'],
    queryFn: async () => {
      const { data, error } = await supabase
        .schema('orders')
        .from('v_invoices_all')
        .select(SELECT_COLS)
        .order('txn_date', { ascending: false });
      if (error) {
        // View not yet pushed or empty — degrade to empty list rather than
        // surfacing a red error to the customer.
        return [];
      }
      return (data ?? []).map((r) => mapRow(r as InvoiceDbRow));
    },
    staleTime: 60 * 1000,
  });
}

/** Single invoice by qbo_invoice_id. */
export function useInvoiceDetail(qboInvoiceId: string | undefined) {
  return useQuery<InvoiceRow | null>({
    queryKey: ['invoices', 'detail', qboInvoiceId ?? null],
    enabled: !!qboInvoiceId,
    queryFn: async () => {
      const { data, error } = await supabase
        .schema('orders')
        .from('v_invoices_all')
        .select(SELECT_COLS)
        .eq('qbo_invoice_id', qboInvoiceId!)
        .maybeSingle();
      if (error || !data) return null;
      return mapRow(data as InvoiceDbRow);
    },
    staleTime: 60 * 1000,
  });
}

export interface InvoiceLine {
  id: number;
  lineNum: number;
  qboItemId: string | null;
  itemName: string | null;
  description: string | null;
  quantity: number | null;
  unitPrice: number | null;
  amount: number;
}

interface InvoiceLineDbRow {
  id: number;
  line_num: number;
  qbo_item_id: string | null;
  item_name: string | null;
  description: string | null;
  quantity: number | string | null;
  unit_price: number | string | null;
  amount: number | string;
}

const LINE_SELECT_COLS =
  'id, line_num, qbo_item_id, item_name, description, quantity, unit_price, amount';

/** Line items for a single invoice, ordered as QBO returned them. */
export function useInvoiceLines(qboInvoiceId: string | undefined) {
  return useQuery<InvoiceLine[]>({
    queryKey: ['invoices', 'lines', qboInvoiceId ?? null],
    enabled: !!qboInvoiceId,
    queryFn: async () => {
      const { data, error } = await supabase
        .schema('orders')
        .from('v_invoice_lines')
        .select(LINE_SELECT_COLS)
        .eq('qbo_invoice_id', qboInvoiceId!)
        .order('line_num', { ascending: true });
      if (error) return [];
      return (data ?? []).map((r): InvoiceLine => {
        const row = r as InvoiceLineDbRow;
        return {
          id: row.id,
          lineNum: row.line_num,
          qboItemId: row.qbo_item_id,
          itemName: row.item_name,
          description: row.description,
          quantity: row.quantity == null ? null : Number(row.quantity),
          unitPrice: row.unit_price == null ? null : Number(row.unit_price),
          amount: Number(row.amount ?? 0),
        };
      });
    },
    staleTime: 60 * 1000,
  });
}
